"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Card, CardBody, CardHeader, CardTitle, Badge, Input, InputGroup, InputGroupText } from "reactstrap"
import DataTable from "react-data-table-component"
import { useNavigate } from "react-router-dom"
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaToggleOff,
  FaToggleOn,
  FaEnvelope,
  FaUserCog,
  FaSearch,
  FaTimes,
} from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import { PersonService } from "../../../Service/person-service"
import type { PersonWithAccount } from "./types"
import Swal from "sweetalert2"

interface PersonListProps {
  onAdd: () => void
  onEdit: (person: PersonWithAccount) => void
  onView: (person: PersonWithAccount) => void
  refreshTrigger: boolean
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

const PersonList: React.FC<PersonListProps> = ({ onAdd, onEdit, onView, refreshTrigger }) => {
  const [data, setData] = useState<PersonWithAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [isSearching, setIsSearching] = useState(false)

  const navigate = useNavigate()

  // Enhanced debounce with live search indicator
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true)
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setIsSearching(false)
    }, 300) // Reduced from 500ms to 300ms for more responsive feel

    return () => clearTimeout(timer)
  }, [search, debouncedSearch])

  // Reset to first page when search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1)
    } else {
      fetchData(1, perPage, debouncedSearch)
    }
  }, [debouncedSearch])

  const fetchData = useCallback(async (page = 1, pageSize = 10, searchTerm = "") => {
    setLoading(true)
    try {
      const params = {
        page,
        page_size: pageSize,
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
      }

      const response: PaginatedResponse<PersonWithAccount> = await PersonService.getAll(params)
      setData(response.results)
      setTotalRows(response.count)
    } catch (error) {
      console.error("Failed to fetch persons:", error)
      Swal.fire("Error", "Failed to load persons.", "error")
      setData([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData(currentPage, perPage, debouncedSearch)
  }, [refreshTrigger, currentPage, perPage, fetchData])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setCurrentPage(page)
  }

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearch(value)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearch("")
  }, [])

  const handleTogglePersonStatus = async (person: PersonWithAccount) => {
    try {
      await PersonService.toggleStatus(person.id)
      Swal.fire({
        title: "Success!",
        text: `Person ${person.status ? "deactivated" : "activated"} successfully`,
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      // Refresh current page
      fetchData(currentPage, perPage, debouncedSearch)
    } catch (error) {
      Swal.fire("Error", "Failed to update person status", "error")
    }
  }

  const handleToggleAccountStatus = async (person: PersonWithAccount) => {
    if (!person.account) {
      Swal.fire("Error", "No account found for this person", "error")
      return
    }
    try {
      await PersonService.toggleAccountStatus(person.id)
      Swal.fire({
        title: "Success!",
        text: `Account ${person.account.is_active ? "deactivated" : "activated"} successfully`,
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      // Refresh current page
      fetchData(currentPage, perPage, debouncedSearch)
    } catch (error) {
      Swal.fire("Error", "Failed to update account status", "error")
    }
  }

  const handleResendVerification = async (person: PersonWithAccount) => {
    try {
      await PersonService.resendVerification(person.id)
      Swal.fire({
        title: "Success!",
        text: "Verification email sent successfully",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
    } catch (error) {
      Swal.fire("Error", "Failed to send verification email", "error")
    }
  }

  const handleDelete = async (person: PersonWithAccount) => {
    const result = await Swal.fire({
      title: "Delete Person?",
      text: `This will permanently remove "${person.full_name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
      focusCancel: true,
    })
    if (!result.isConfirmed) return

    try {
      await PersonService.delete(person.id)
      await Swal.fire({
        title: "Deleted!",
        text: "Person has been removed.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      // Smart page navigation after deletion
      const newTotalRows = totalRows - 1
      const maxPage = Math.ceil(newTotalRows / perPage)
      const targetPage = currentPage > maxPage ? Math.max(1, maxPage) : currentPage
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage)
      } else {
        fetchData(currentPage, perPage, debouncedSearch)
      }
    } catch (error) {
      Swal.fire("Error", "Failed to delete person.", "error")
    }
  }

  const handleSetRoles = (person: PersonWithAccount) => {
    if (!person.has_account || !person.account) {
      Swal.fire("Error", "No account found for this person", "error")
      return
    }
    navigate(`/user-role-management/${person.account.id}`)
  }

  const columns = [
    {
      name: "Person",
      cell: (person: PersonWithAccount) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-medium">{person.full_name}</div>
          </div>
        </div>
      ),
      sortable: true,
      selector: (person: PersonWithAccount) => person.full_name,
    },
    {
      name: "Contact",
      cell: (person: PersonWithAccount) => (
        <div>
          {person.email && <div className="small">{person.email}</div>}
          {person.phone && <div className="small text-muted">{person.phone}</div>}
        </div>
      ),
      selector: (person: PersonWithAccount) => person.email || "",
    },
    {
      name: "Account Status",
      cell: (person: PersonWithAccount) => (
        <div>
          {person.has_account ? (
            <div className="d-flex flex-column gap-1">
              <Badge color={person.account?.is_active ? "success" : "danger"}>
                {person.account?.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          ) : (
            <Badge color="secondary">No Account</Badge>
          )}
        </div>
      ),
      sortable: true,
      selector: (person: PersonWithAccount) =>
        person.has_account ? (person.account?.is_active ? "Active" : "Inactive") : "No Account",
    },
    {
      name: "Created",
      cell: (person: PersonWithAccount) => <small>{new Date(person.created_at).toLocaleDateString()}</small>,
      sortable: true,
      selector: (person: PersonWithAccount) => person.created_at,
    },
    {
      name: "Actions",
      cell: (person: PersonWithAccount) => (
        <div className="d-flex gap-1">
          <Btn color="info" size="xs" onClick={() => onView(person)} title="View Details">
            <FaEye size={12} />
          </Btn>
          {person.has_account && person.account && (
            <Btn color="primary" size="xs" onClick={() => handleSetRoles(person)} title="Manage User Roles">
              <FaUserCog size={12} />
            </Btn>
          )}
          <Btn color="warning" size="xs" onClick={() => onEdit(person)} title="Edit">
            <FaEdit size={12} />
          </Btn>
          {person.has_account && person.account && (
            <Btn
              color={person.account.is_active ? "secondary" : "success"}
              size="xs"
              onClick={() => handleToggleAccountStatus(person)}
              title={person.account.is_active ? "Deactivate Account" : "Activate Account"}
            >
              {person.account.is_active ? <FaToggleOff size={12} /> : <FaToggleOn size={12} />}
            </Btn>
          )}
          {person.has_account && person.account && !person.account.email_verified && (
            <Btn color="primary" size="xs" onClick={() => handleResendVerification(person)} title="Resend Verification">
              <FaEnvelope size={12} />
            </Btn>
          )}
          <Btn color="danger" size="xs" onClick={() => handleDelete(person)} title="Delete">
            <FaTrash size={12} />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "230px",
    },
  ]

  // Enhanced loading component
  const LoadingComponent = () => (
    <div className="d-flex justify-content-center align-items-center py-4">
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <CardTitle tag="h4" className="mb-1">
              Users Management
            </CardTitle>
            <p className="text-muted mb-0">
              Manage users and their associated accounts ({totalRows} total)
              {search && <span className="text-primary"> - Filtered by "{search}"</span>}
            </p>
          </div>
          <Btn color="primary" onClick={onAdd}>
            <FaPlus className="me-2" />
            Add User
          </Btn>
        </div>
      </CardHeader>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <InputGroup style={{ maxWidth: 350 }}>
            <InputGroupText>
              <FaSearch className={isSearching ? "text-primary" : "text-muted"} />
            </InputGroupText>
            <Input
              type="search"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={handleSearchChange}
              autoComplete="off"
            />
            {search && (
              <InputGroupText style={{ cursor: "pointer" }} onClick={handleClearSearch} title="Clear search">
                <FaTimes className="text-muted" />
              </InputGroupText>
            )}
          </InputGroup>

          {/* Live search indicator */}
          {isSearching && (
            <div className="d-flex align-items-center text-muted">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="sr-only">Searching...</span>
              </div>
              <small>Searching...</small>
            </div>
          )}
        </div>

        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          progressComponent={<LoadingComponent />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          striped
          responsive
          customStyles={{
            headCells: {
              style: {
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
                fontSize: "14px",
                color: "#495057",
              },
            },
            rows: {
              style: {
                minHeight: "56px",
                fontSize: "14px",
              },
              stripedStyle: {
                backgroundColor: "#f8f9fa",
              },
            },
            cells: {
              style: {
                paddingLeft: "12px",
                paddingRight: "12px",
              },
            },
          }}
          noDataComponent={
            <div className="text-center py-4">
              <div className="text-muted">
                {search ? `No persons match your search criteria "${search}"` : "No persons found"}
              </div>
              {search && (
                <small className="text-muted d-block mt-2">
                  Try adjusting your search terms or{" "}
                  <span className="text-primary" style={{ cursor: "pointer" }} onClick={handleClearSearch}>
                    clear the search
                  </span>
                </small>
              )}
            </div>
          }
        />
      </CardBody>
    </Card>
  )
}

export default PersonList
